/* eslint-disable @angular-eslint/prefer-inject */
import { InventoryBundleDataService } from '../infrastructure/inventory-bundles/inventory-bundle.data.service';
import {
    InventoryBundleCreate,
    InventoryBundleDto,
    InventoryBundleRead,
    InventoryBundleSearchParams,
} from '../entities/inventory-bundle.dto';
import { InventoryBundleStore } from '../infrastructure/inventory-bundles/inventory-bundle.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { InventoryBundleMapper } from '../infrastructure/inventory-bundles/inventory-bundle.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { IQueryParams, SafeAny } from '@pf/shared-common';
import { WarehouseFacade } from './warehouse.facade';
import { map, Observable } from 'rxjs';
import { Operation } from 'fast-json-patch';
import {
    InventoryAdjustmentReason,
    InventoryBundleCondition,
    InventoryBundleReserveRequestParams,
    PickStrategy,
    ResourceAuth,
    SlotType,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';

@Injectable()
export class InventoryBundleFacade extends AbstractManageEntityFacade<
    InventoryBundleDto,
    InventoryBundleRead,
    InventoryBundleCreate,
    InventoryBundleSearchParams
> {
    private readonly warehouseFacade = inject(WarehouseFacade);
    nameField: keyof InventoryBundleDto = 'name';

    constructor(
        dataService: InventoryBundleDataService,
        store: InventoryBundleStore,
        mapper: InventoryBundleMapper
    ) {
        super(dataService, null, store, mapper);
    }

    override getDefaultSearchParams$(
        params: IQueryParams<InventoryBundleDto>
    ): Observable<IQueryParams<InventoryBundleDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: InventoryBundleCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof InventoryBundleDto, string>> {
        return { name: searchText };
    }

    adjustInventory$(
        bundleId: string,
        adjustByQuantity: number,
        reason: InventoryAdjustmentReason,
        notes?: string | null
    ) {
        return (this.dataService as InventoryBundleDataService)
            .adjustInventory$({
                inventoryBundleId: bundleId,
                adjustByQuantity,
                adjustmentReason: reason,
                notes: notes || undefined,
            })
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    adjustInventoryForSourceEntity$(
        bundleId: string,
        adjustByQuantity: number,
        sourceEntity: WmsPlatformEntity,
        sourceEntityId: string
    ) {
        return (this.dataService as InventoryBundleDataService)
            .adjustInventory$({
                inventoryBundleId: bundleId,
                adjustByQuantity,
                sourceEntity,
                sourceEntityId,
                adjustmentReason: InventoryAdjustmentReason.Exception,
            })
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    adjustInventoryForException$(
        bundleId: string,
        adjustByQuantity: number,
        exceptionId: string,
        reason: InventoryAdjustmentReason = InventoryAdjustmentReason.Exception
    ) {
        return (this.dataService as InventoryBundleDataService)
            .adjustInventory$({
                inventoryBundleId: bundleId,
                adjustByQuantity,
                exceptionId,
                adjustmentReason: reason,
            })
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    partialMoveForException$(
        bundleId: string,
        destinationContainerId: string,
        quantityToMove: number,
        pickStrategy: PickStrategy,
        exceptionId: string
    ) {
        return (this.dataService as InventoryBundleDataService)
            .partialMove$(bundleId, {
                containerId: destinationContainerId,
                quantity: quantityToMove,
                pickStrategy,
                exceptionId,
                adjustmentReason: InventoryAdjustmentReason.Exception,
            })
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    withdrawInventoryForException$(
        bundleId: string,
        quantityToWithdraw: number,
        pickStrategy: PickStrategy,
        exceptionId: string
    ) {
        return (this.dataService as InventoryBundleDataService)
            .withdrawInventory$(bundleId, {
                withdrawQuantity: quantityToWithdraw,
                pickStrategy,
                exceptionId,
                adjustmentReason: InventoryAdjustmentReason.Exception,
            })
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    getBundleByItemIdAndSlotId$(itemId: string, slotId: string) {
        return this.oneTimeSearch$({
            pageSize: 1,
            pageIndex: 1,
            filters: {
                item: itemId,
                slotId,
            } as SafeAny,
        }).pipe(
            map(result => {
                return result.data.length > 0 ? result.data[0] : null;
            })
        );
    }

    moveBundle$(
        bundleId: string,
        containerId: string,
        reason: InventoryAdjustmentReason,
        notes?: string | null
    ) {
        return (this.dataService as InventoryBundleDataService)
            .move$(bundleId, containerId, reason, notes || undefined)
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    moveBundleSimple$(bundleId: string, containerId: string) {
        return (this.dataService as InventoryBundleDataService)
            .moveBundleSimple$(bundleId, containerId)
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    getInventoryInfo$(itemId: string) {
        return (
            this.dataService as InventoryBundleDataService
        ).getInventoryInfo$(itemId, this.warehouseFacade.active!.id);
    }

    refreshBundlesForContainers$(containerIds: string[]) {
        return (
            this.dataService as InventoryBundleDataService
        ).refreshBundlesForContainers$(containerIds);
    }

    getBundlesByItemAndSlotType$(itemId: string, slotType: SlotType) {
        return this.oneTimeSearch$({
            pageIndex: 1,
            pageSize: 50,
            sortField: 'createdDate',
            sortOrder: 'ascend',
            filters: {
                item: [itemId],
                slotType: [slotType],
                containedInSlot: true,
            } as Partial<Record<string, string | string[] | boolean>>,
        });
    }

    createReservation$(reservation: InventoryBundleReserveRequestParams) {
        return (this.dataService as InventoryBundleDataService)
            .reserveInventory$(reservation)
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }

    getAllBySlotIds$(slotIds: string[]) {
        return this.searchAll$(
            {
                pageSize: 50,
                pageIndex: 1,
                filters: {
                    slotId: slotIds,
                } as SafeAny,
            },
            false
        );
    }

    updateCondition$(
        bundleId: string,
        condition: InventoryBundleCondition
    ): Observable<InventoryBundleDto> {
        const operations: Operation[] = [
            {
                op: 'replace',
                path: '/condition',
                value: condition,
            },
        ];

        return this.dataService
            .update$(bundleId, operations, {
                condition,
            } as InventoryBundleCreate)
            .pipe(map(bundle => this.mapper.toDTO(bundle)));
    }
}
